import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { ErrorState, LoadingState } from '../components/primitives';
import SessionRubric from '../components/session/SessionRubric';
import SessionTranscript from '../components/session/SessionTranscript';
import SessionStats from '../components/session/SessionStats';
import { clientPath, rangeFromQuery, repSessionsPath } from '../lib/rangeParams';
import {
  analysisLabel,
  analysisTone,
  formatDuration,
  formatSessionTime,
  sourceLabel
} from '../lib/session';

/**
 * One call, reviewed without logging into the tenant's own admin portal.
 *
 * Same three views the tenant admin gets — rubric, transcript, statistics —
 * built on this portal's tokens. READ-ONLY: re-analysis, rubric reweighting and
 * knowledge-base writes stay with the tenant admin who owns the call and whose
 * name belongs in its audit trail.
 */

type Tab = 'rubric' | 'transcript' | 'statistics';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'rubric', label: 'Call insights' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'statistics', label: 'Statistics' }
];

export default function SessionReview() {
  const { tenantId, sessionId } = useParams<{ tenantId: string; sessionId: string }>();
  const [searchParams] = useSearchParams();
  const range = rangeFromQuery(searchParams);

  const [activeTab, setActiveTab] = useState<Tab>('rubric');
  const [scrollToTimestamp, setScrollToTimestamp] = useState<string | null>(null);

  const { data, loading, error } = useApiData(
    () => api.getSession(tenantId as string, sessionId as string),
    [tenantId, sessionId]
  );

  // Rubric evidence -> the moment in the transcript. Switching tabs first means
  // the transcript is mounted by the time it tries to scroll.
  const handleNavigateToTimestamp = (iso: string) => {
    setActiveTab('transcript');
    setScrollToTimestamp(iso);
  };

  if (loading) return <LoadingState label="Loading session…" />;
  if (error || !data?.data) return <ErrorState message={error || 'Session not found.'} />;

  const { session, displayName } = data.data;
  const analysisStatus = session.callInsights?.status ?? null;

  return (
    <>
      <div className="filter-bar">
        {/* A session with no userId cannot address a rep's session list, so it
            falls back to the client itself rather than a route that 404s. */}
        <Link
          className="dd"
          to={
            session.userId
              ? repSessionsPath(tenantId as string, session.userId, range)
              : clientPath(tenantId as string, range)
          }
        >
          ← {session.userId ? 'All sessions' : displayName}
        </Link>
        <span className="sep" />
        <span className="label">Client</span>
        <span className="dd-pill">{displayName}</span>
      </div>

      <div className="client-hero">
        <div>
          <div className="eyebrow">Sessions · review</div>
          <h1 className="title" style={{ fontSize: 40, margin: '0 0 4px' }}>
            <span className="serif">{session.sessionName}</span>
          </h1>
          <div className="meta-row">
            <span>{session.userEmail || session.userId || '—'}</span>
            <span style={{ color: 'var(--ink-4)' }}>·</span>
            <span>{formatSessionTime(session.callStartTime)} UTC</span>
            <span style={{ color: 'var(--ink-4)' }}>·</span>
            <span>{formatDuration(session.callDuration)}</span>
            <span style={{ color: 'var(--ink-4)' }}>·</span>
            <span>{sourceLabel(session.source, session.platform)}</span>
            {session.roomName && (
              <>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span>{session.roomName}</span>
              </>
            )}
          </div>
          <p className="subtitle">
            Read-only. Re-running the analysis or retuning this call&rsquo;s rubric weights is done
            from {displayName}&rsquo;s own admin portal.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <span className={'chip ' + analysisTone(analysisStatus)}>
            Analysis: {analysisLabel(analysisStatus)}
          </span>
          {session.multipleSalespeople && <span className="chip info">Team-selling call</span>}
        </div>
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={'tab' + (activeTab === tab.id ? ' on' : '')}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'rubric' && (
        <SessionRubric
          rubricAnalysis={session.callInsights?.rubricAnalysis}
          multipleSalespeople={session.multipleSalespeople}
          onNavigateToTimestamp={handleNavigateToTimestamp}
        />
      )}
      {activeTab === 'transcript' && (
        <SessionTranscript
          transcripts={session.transcripts}
          participants={session.participants}
          scrollToTimestamp={scrollToTimestamp}
        />
      )}
      {activeTab === 'statistics' && <SessionStats session={session} />}
    </>
  );
}
